import { Link } from "react-router-dom";
import { Emblem } from "./Brand";
import { SITE } from "../data/site";
import { BookingLink } from "./BookingLink";
import { PhoneLink, TextLink } from "./PhoneLink";
import { Mail, Phone, MapPin, Clock, Facebook } from "lucide-react";

export const Footer = () => (
  <footer data-testid="footer" className="bg-navy text-parchment relative overflow-hidden">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-20 relative">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4">
            {/* Ivory coin keeps the navy emblem legible on the navy footer */}
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full shrink-0" style={{ background: "#FDFBEE" }}>
              <Emblem sizes="52px" testId="footer-logo-img" className="w-[52px] h-[52px] object-contain" />
            </span>
            <span className="font-serif text-2xl text-parchment tracking-tight leading-none">Oakford Ledger</span>
          </div>
          <p data-testid="footer-tagline" className="mt-6 text-parchment max-w-sm leading-relaxed text-[15px] opacity-85">
            {SITE.footerTagline}
          </p>
          <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" aria-label="Oakford Ledger on Facebook"
            data-testid="footer-facebook-link"
            className="inline-flex items-center gap-2 mt-6 min-h-[44px] text-parchment hover:text-gold transition-colors">
            <Facebook size={18} /> <span className="font-mono text-[13px] uppercase tracking-[0.14em]">Facebook</span>
          </a>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-gold mb-5">Navigate</p>
          <ul className="space-y-0 text-[15px] text-parchment opacity-85">
            <li><Link to="/" className="inline-flex items-center min-h-[44px] hover:text-gold transition-colors">Home</Link></li>
            <li><Link to="/services" className="inline-flex items-center min-h-[44px] hover:text-gold transition-colors">Services</Link></li>
            <li><Link to="/about" className="inline-flex items-center min-h-[44px] hover:text-gold transition-colors">About</Link></li>
            <li><Link to="/contact" className="inline-flex items-center min-h-[44px] hover:text-gold transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-gold mb-5">Get in touch</p>
          <ul className="space-y-4 text-[15px] text-parchment opacity-90">
            <li className="flex items-center gap-3">
              <Mail size={15} className="text-gold shrink-0" />
              <a href={`mailto:${SITE.email}`} className="hover:text-gold transition-colors" data-testid="footer-email-link">{SITE.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={15} className="text-gold shrink-0 mt-1" />
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-parchment-muted">{SITE.phoneLabel}</span>
                <PhoneLink placement="footer" className="hover:text-gold transition-colors" />
                <span className="text-parchment-muted mx-2">·</span>
                <TextLink placement="footer" className="font-mono text-[12px] uppercase tracking-[0.14em] text-gold hover:text-gold-hover transition-colors" />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="text-gold shrink-0 mt-1" />
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-parchment-muted">{SITE.addressLabel}</span>
                {SITE.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Clock size={15} className="text-gold shrink-0 mt-1" />
              <span>
                {SITE.hours.map((h) => <span key={h.d} className="block">{h.d}: {h.t}</span>)}
                <span className="block text-parchment-muted">{SITE.timezone}</span>
              </span>
            </li>
          </ul>
          <BookingLink placement="footer" testId="footer-consultation-btn" className="inline-flex items-center mt-7 min-h-[44px] bg-gold text-navy px-6 py-3 font-mono text-[13px] uppercase tracking-[0.14em] hover:bg-gold-hover transition-colors">
            Book a Consultation
          </BookingLink>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-[#24355A] flex flex-col sm:flex-row justify-between gap-4 text-parchment-muted font-mono text-[12px] uppercase tracking-[0.14em]">
        <span>© {new Date().getFullYear()} Oakford Ledger</span>
        <span data-testid="footer-service-area">Based in Arizona · Serving small businesses nationwide</span>
        <nav aria-label="Policies" data-testid="footer-policy-links" className="flex flex-wrap items-center normal-case tracking-normal font-sans text-[14px]">
          {[["/privacy-policy", "Privacy Policy", "footer-privacy-link"], ["/terms-and-conditions", "Terms and Conditions", "footer-terms-link"], ["/cookie-policy", "Cookie Policy", "footer-cookie-link"]].map(([to, label, tid]) => (
            <Link key={to} to={to} data-testid={tid}
              className="inline-flex items-center min-h-[44px] text-parchment hover:text-gold transition-colors [&:not(:last-child)]:after:content-['|'] [&:not(:last-child)]:after:mx-3 [&:not(:last-child)]:after:text-parchment-muted">
              {label}
            </Link>
          ))}
        </nav>
      </div>
    </div>
  </footer>
);
